import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ExternalLink, Plus, Trash2, Tv } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";

const SLIDES = [
  ["now_playing", "Now Playing"], ["up_next", "Upcoming Sessions"], ["free_slots", "Free Slots Now"],
  ["free_bands", "Free Time Bands"], ["games", "Game Showcase"],
  ["food", "Food & Beverages"], ["promos", "Promotions"], ["announcements", "Announcements"],
  ["instagram", "Instagram QR"], ["video", "Promo Video"],
];

export default function LivePreviewTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/display").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <Loader2 className="animate-spin text-purple-400" />;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const setSlide = (k, v) => setCfg({ ...cfg, slides: { ...cfg.slides, [k]: v } });
  const editList = (key, i, field, v) => { const l = [...cfg[key]]; l[i] = { ...l[i], [field]: v }; set(key, l); };
  const addItem = (key) => set(key, [...(cfg[key] || []), { title: "New", text: "", active: true }]);
  const rmItem = (key, i) => set(key, cfg[key].filter((_, x) => x !== i));

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/display", cfg); setCfg(r.data); toast.success("Display settings saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const openScreen = () => window.open(`${window.location.origin}/display`, "_blank", "noopener");

  const ListEditor = ({ title, keyName }) => (
    <div className="glass rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3"><p className="font-semibold">{title}</p>
        <Button size="sm" onClick={() => addItem(keyName)} data-testid={`add-${keyName}`} className="h-8 bg-purple-600 hover:bg-purple-500"><Plus size={13} /> Add</Button></div>
      <div className="space-y-2">
        {(cfg[keyName] || []).map((p, i) => (
          <div key={i} className="flex gap-2 items-center flex-wrap">
            <Input value={p.title} onChange={(e) => editList(keyName, i, "title", e.target.value)} placeholder="Title" className="h-9 w-40 bg-purple-950/30 border-purple-500/30" />
            <Input value={p.text} onChange={(e) => editList(keyName, i, "text", e.target.value)} placeholder="Text" className="h-9 flex-1 min-w-[180px] bg-purple-950/30 border-purple-500/30" />
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={p.active !== false} onChange={(e) => editList(keyName, i, "active", e.target.checked)} /> On</label>
            <Button size="sm" variant="outline" onClick={() => rmItem(keyName, i)} className="h-9 border-red-500/30 bg-transparent text-red-300"><Trash2 size={13} /></Button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="glass rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3"><Tv className="text-purple-300" size={24} /><div><h3 className="font-display font-bold">Live Preview — Second Screen</h3><p className="text-sm text-slate-400">Open this on your HDMI TV, then press F11 for full screen.</p></div></div>
        <Button onClick={openScreen} data-testid="open-second-screen" className="h-11 bg-emerald-600 hover:bg-emerald-500"><ExternalLink size={16} /> Open Second Screen</Button>
      </div>

      <div className="glass rounded-2xl p-5 grid sm:grid-cols-2 gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="display-enabled" /> Display Enabled</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cfg.show_customer_name} onChange={(e) => set("show_customer_name", e.target.checked)} data-testid="display-show-name" /> Show customer name on screen</label>
        <div><label className="text-xs text-purple-400 font-mono">Rotation seconds</label>
          <select value={cfg.rotation_seconds} onChange={(e) => set("rotation_seconds", Number(e.target.value))} data-testid="display-rotation" className="h-10 w-full mt-1 rounded-xl bg-purple-950/40 border border-purple-500/30 px-3 text-sm">
            {[5, 10, 15, 20, 30].map((s) => <option key={s} value={s}>{s} seconds</option>)}
          </select></div>
      </div>

      <div className="glass rounded-2xl p-5">
        <p className="font-semibold mb-3">Slides to show</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SLIDES.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cfg.slides[k] !== false} onChange={(e) => setSlide(k, e.target.checked)} data-testid={`slide-${k}`} /> {label}</label>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-5 grid sm:grid-cols-2 gap-4">
        <div><label className="text-xs text-purple-400 font-mono">Instagram handle</label><Input value={cfg.instagram_handle} onChange={(e) => set("instagram_handle", e.target.value)} className="h-10 mt-1 bg-purple-950/30 border-purple-500/30" /></div>
        <div><label className="text-xs text-purple-400 font-mono">Instagram URL (QR)</label><Input value={cfg.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} className="h-10 mt-1 bg-purple-950/30 border-purple-500/30" /></div>
        <div className="sm:col-span-2"><label className="text-xs text-purple-400 font-mono">Promo video URL (YouTube or mp4)</label><Input value={cfg.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://youtu.be/..." className="h-10 mt-1 bg-purple-950/30 border-purple-500/30" /></div>
      </div>

      <ListEditor title="Promotions" keyName="promos" />
      <ListEditor title="Announcements" keyName="announcements" />

      <Button onClick={save} disabled={saving} data-testid="display-save" className="h-12 w-full bg-purple-600 hover:bg-purple-500 font-semibold">{saving ? <Loader2 className="animate-spin" /> : "Save Display Settings"}</Button>
    </div>
  );
}
